<template>
    <div id="emap_menu">
        <v-tabs
            centered
            background-color="#353b4e"
            color="white"
            v-model="sub_menu_selected"
            class="sub_menu"
        >
            <v-tab
                v-for="submenu in sub_menu"
                :key="`submenu${submenu.order}`"
                :href="`#sub-menu-${submenu.order}`"
                class="menu-tab"
                :disabled="submenu.disable"
            >
                {{ submenu.text }}
            </v-tab>
        </v-tabs>
        <v-tabs-items v-model="sub_menu_selected">
            <v-tab-item value="sub-menu-1"
                class="sub_menu_content"
            >
                <ForecastObservation
                    ref="forecast_observation"
                    @toggle_time_choose="toggle_time_choose"
                    @check-background-menu-from-emap-menu="check_background_menu"
                    @transfer-information="(information) => $emit('transfer-information', information)"
                    @trigger-display-function="update_emap_content"
                />
            </v-tab-item>
            <v-tab-item 
                value="sub-menu-2" 
                class="sub_menu_content"
                eager
            >
                <Geomap
                    v-if="has_got_map_config_from_server"
                    ref="background"
                    @trigger-display-function="update_emap_content"
                /> 
            </v-tab-item>
            <v-tab-item value="sub-menu-3" 
                class="sub_menu_content"
            >
                <TimeChoose
                    ref="time_choose"
                    :is_hide_history="is_hide_history"
                    @trigger-display-function="update_emap_content"
                />
            </v-tab-item>
        </v-tabs-items> 
        <DisplayCtrlFooter 
            @trigger-display-function="update_emap_content"
            :text="$t('__map_obs.show_pic_data')"
        />
    </div>
</template>


<script>
    import { get_map_config_ajax } from '@/utils/emap/emap_init.js'
    import { mapState, mapMutations, mapGetters } from 'vuex';
    import isEqual from 'lodash/isEqual';
    
    import ForecastObservation from '@/components/emap/menu/forecast_observation.vue';
    import Geomap from '@/components/emap/menu/geomap.vue';

    export default {
        name: 'AllEmapMenu',

        components: {
            ForecastObservation, 
            Geomap, // 如果使用 arrow function 引入 component ， 則無法使用 $refs 抓到該component
            TimeChoose: () => import('@/components/emap/menu/time_choose.vue'),
            DisplayCtrlFooter: () => import('@/components/emap/menu/display_ctrl_footer.vue')
        },

        data() {
            return {
                sub_menu_selected: 'sub-menu-1',
                //個案
                case_by_year_obj: {},

                is_hide_history: false,
            }
        },
        
        computed: {
            ...mapState([
                'client_name',
                'language',
            ]),

            ...mapState('emap', [
                'has_got_map_config_from_server',
                'time_choose_parameters'
            ]),

            ...mapGetters([
                'api_language_parameters',
                'is_mobile_width'
            ]),
            
            sub_menu() {
                return [
                    {'text': `${this.$t('__menu.product')}`, 'order': '1', 'disable': false},
                    {'text': `${this.$t('__menu.background')}`, 'order': '2', 'disable': false},
                    {'text': `${this.$t('__menu.setting')}`, 'order': '3', 'disable': false}
                ]
            },

            is_hide_menu: {
                get() {
                    return this.$store.state.emap.is_hide_menu
                },

                set(value) {
                    this.$store.commit('emap/set_is_hide_menu', value)
                }
            },
        },

        watch: {
            language: {
                immediate: true,
                async handler() {
                    await this.init_map_config();
                }
            },
        },

        methods: {
            ...mapMutations('emap', [
                'set_map_config_from_server',
                'set_has_got_map_config_from_server',
                'set_time_choose_parameters',
            ]),

            // all for case
            ...mapMutations({
                set_atlas_loaded: 'atlas/set_atlas_loaded',
                set_monitor_loaded: 'monitor/set_monitor_loaded',
                set_case_selected: 'case/set_case_selected',
            }),
            
            async init_map_config() {
                try {
                    const response = await get_map_config_ajax(this.client_name, this.api_language_parameters);
                    this.set_map_config_from_server(response.data ?? {})
                } catch(err) {
                    console.error(err)
                }
                this.set_has_got_map_config_from_server(true)
            },                
            
            toggle_time_choose(time_choose_visible_state) {
                // 尚需添加 css
                this.is_hide_history = !time_choose_visible_state;
            },

            check_background_menu(background_menu_related_ids) {
                this.$refs.background.check_related_id_from_emap_menu(background_menu_related_ids)
            },

            async update_emap_content() {
                if (this.is_mobile_width) this.is_hide_menu = true
                const forecast_observation_selected_items = this.$refs.forecast_observation._generate_selected_items();
                const background_selected_items = this.$refs.background._generate_selected_items();

                if (!forecast_observation_selected_items.length && !background_selected_items.length) 
                    return alert(this.$t('__map_obs.no_check_any_option_alert'));

                // 有時間更動的話，會先更新 webmap，更新完 webmap 後本來就會重繪地圖，因此不需要執行 show_emap_data
                if (this.$refs.time_choose) {
                    const new_time_choose_parameters = this.$refs.time_choose.compute_time_choose_parameters()
                    const is_user_change_time = !isEqual(this.time_choose_parameters, new_time_choose_parameters)
                    if (is_user_change_time) {
                        this._handle_case(new_time_choose_parameters)
                        this.set_time_choose_parameters(new_time_choose_parameters)
                    } else {
                        this.$refs.forecast_observation.show_emap_data()    
                    }
                } else {
                    this.$refs.forecast_observation.show_emap_data()
                }
                this.$refs.background.update_background()
            },

            _handle_case(new_time_choose_parameters) {
                const is_need_handle_case = [this.time_choose_parameters.status, new_time_choose_parameters.status] .includes('case')
                if (is_need_handle_case) {
                    const new_case_selected = new_time_choose_parameters.case_selected
                    this.set_monitor_loaded(false);
                    this.set_atlas_loaded(false);
                    this.set_case_selected(new_case_selected)
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
    .menu-tab {
        padding: 0 !important;
        color: var(--grey-pipe) !important;
        touch-action: none;
    }
</style>

<style scoped>
    .sub_menu >>> .v-tabs-slider {
        background-image: linear-gradient(to right, #2366f8 0%, #2cfde1 100%);
    }

    .sub_menu >>> .v-tab--active {
        color: #fff !important;
    }

    .sub_menu >>> .v-slide-group__prev {
        display: none !important;
    }

    .sub_menu >>> .v-slide-group__next {
        display: none;
    }

    .sub_menu_content {
        overflow-x: hidden;
        overflow-y: auto;
        background-color: var(--btn-menu-background-color);
    }
</style>
